import React from 'react';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import Header from '@/components/Header';

export default function Datenschutz() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-12 max-w-4xl" role="main">
        <nav aria-label="Breadcrumb" className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-primary hover:text-primary-hover transition-colors text-sm font-medium mb-6"
          >
            ← Zurück zum Anamnese Generator
          </Link>
        </nav>

        <div className="bg-card border border-border rounded-lg shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground">Datenschutz&shy;erklärung</h1>
          </div>

          <div className="prose prose-slate max-w-none text-foreground">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">1. Datenschutz auf einen Blick</h2>
                <h3 className="text-xl font-semibold text-foreground mb-3">Allgemeine Hinweise</h3>
                <p className="leading-relaxed mb-4">
                  Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können. Ausführliche Informationen zum Thema Datenschutz entnehmen Sie unserer unter diesem Text aufgeführten Datenschutzerklärung.
                </p>

                <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Datenerfassung auf dieser Website</h3>
                <h4 className="text-lg font-semibold text-foreground mb-2">Wer ist verantwortlich für die Datenerfassung auf dieser Website?</h4>
                <p className="leading-relaxed mb-4">
                  Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten können Sie dem Abschnitt "Hinweis zur Verantwortlichen Stelle" in dieser Datenschutzerklärung entnehmen.
                </p>

                <h4 className="text-lg font-semibold text-foreground mb-2">Wie erfassen wir Ihre Daten?</h4>
                <p className="leading-relaxed mb-4">
                  Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z. B. um Daten handeln, die Sie in ein Kontaktformular eingeben.
                </p>
                <p className="leading-relaxed mb-4">
                  Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der Website durch unsere IT-Systeme erfasst. Das sind vor allem technische Daten (z. B. Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs). Die Erfassung dieser Daten erfolgt automatisch, sobald Sie diese Website betreten.
                </p>

                <h4 className="text-lg font-semibold text-foreground mb-2">Wofür nutzen wir Ihre Daten?</h4>
                <p className="leading-relaxed mb-4">
                  Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden.
                </p>

                <h4 className="text-lg font-semibold text-foreground mb-2">Welche Rechte haben Sie bezüglich Ihrer Daten?</h4>
                <p className="leading-relaxed mb-4">
                  Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung oder Löschung dieser Daten zu verlangen.
                </p>
              </section>

              <section className="pt-6 border-t border-border">
                <h2 className="text-2xl font-semibold text-foreground mb-4">2. Hosting</h2>
                <h3 className="text-xl font-semibold text-foreground mb-3">Externes Hosting</h3>
                <p className="leading-relaxed mb-4">
                  Diese Website wird extern gehostet. Die personenbezogenen Daten, die auf dieser Website erfasst werden, werden auf den Servern des Hosters gespeichert.
                </p>
                <p className="leading-relaxed mb-4">
                  Wir setzen folgenden Hoster ein:
                </p>
                <p className="leading-relaxed mb-4">
                  netcup GmbH<br />
                  Emmy-Noether-Straße 10<br />
                  D-76131 Karlsruhe
                </p>
              </section>

              <section className="pt-6 border-t border-border">
                <h2 className="text-2xl font-semibold text-foreground mb-4">3. Allgemeine Hinweise und Pflichtinformationen</h2>
                <h3 className="text-xl font-semibold text-foreground mb-3">Datenschutz</h3>
                <p className="leading-relaxed mb-4">
                  Die Betreiber dieser Seiten nehmen den Schutz Ihrerpersönlichen Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung.
                </p>

                <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Hinweis zur verantwortlichen Stelle</h3>
                <p className="leading-relaxed mb-4">
                  Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:
                </p>
                <p className="leading-relaxed mb-4">
                  Jonas Parlow / Creativey Media<br />
                  c/o Block Services<br />
                  Stuttgarter Str. 106<br />
                  70736 Fellbach
                </p>
                <p className="leading-relaxed mb-4">
                  Telefon: <a href="tel:01759926145" className="text-primary hover:text-primary-hover">0175 9926145</a><br />
                  E-Mail: <a href="mailto:info@creativey.media" className="text-primary hover:text-primary-hover">info@creativey.media</a>
                </p>
              </section>

              <section className="pt-6 border-t border-border">
                <h2 className="text-2xl font-semibold text-foreground mb-4">4. Datenerfassung auf dieser Website</h2>
                <h3 className="text-xl font-semibold text-foreground mb-3">Cookies</h3>
                <p className="leading-relaxed mb-4">
                  Unsere Internetseiten verwenden so genannte "Cookies". Cookies sind kleine Datenpakete und richten auf Ihrem Endgerät keinen Schaden an. Sie werden entweder vorübergehend für die Dauer einer Sitzung (Session-Cookies) oder dauerhaft (permanente Cookies) auf Ihrem Endgerät gespeichert.
                </p>

                <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">CCM19</h3>
                <p className="leading-relaxed mb-4">
                  Unsere Website nutzt CCM19, um Ihre Einwilligung zur Speicherung bestimmter Cookies auf Ihrem Endgerät oder zum Einsatz bestimmter Technologien einzuholen und diese datenschutzkonform zu dokumentieren. Anbieter dieser Technologie ist Papoo Software & Media GmbH, Auguststr. 4, 53229 Bonn.
                </p>
              </section>

              <section className="pt-6 border-t border-border">
                <h2 className="text-2xl font-semibold text-foreground mb-4">5. Analyse-Tools und Werbung</h2>
                <h3 className="text-xl font-semibold text-foreground mb-3">Google Tag Manager</h3>
                <p className="leading-relaxed mb-4">
                  Wir setzen den Google Tag Manager ein. Anbieter ist die Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland.
                </p>

                <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Google Analytics</h3>
                <p className="leading-relaxed mb-4">
                  Diese Website nutzt Funktionen des Webanalysedienstes Google Analytics. Anbieter ist die Google Ireland Limited ("Google"), Gordon House, Barrow Street, Dublin 4, Irland.
                </p>
                <p className="leading-relaxed mb-4">
                  Google Analytics ermöglicht es dem Websitebetreiber, das Verhalten der Websitebesucher zu analysieren. Die Nutzung dieses Dienstes erfolgt auf Grundlage Ihrer Einwilligung nach Art. 6 Abs. 1 lit. a DSGVO und § 25 Abs. 1 TDDDG.
                </p>

                <h4 className="text-lg font-semibold text-foreground mb-2">IP Anonymisierung</h4>
                <p className="leading-relaxed mb-4">
                  Die Google Analytics IP-Anonymisierung ist aktiviert. Dadurch wird Ihre IP-Adresse von Google innerhalb von Mitgliedstaaten der Europäischen Union vor der Übermittlung in die USA gekürzt.
                </p>

                <h4 className="text-lg font-semibold text-foreground mb-2">Browser Plugin</h4>
                <p className="leading-relaxed mb-4">
                  Sie können die Erfassung und Verarbeitung Ihrer Daten durch Google verhindern, indem Sie das unter dem folgenden Link verfügbare Browser-Plugin herunterladen und installieren:{' '}
                  <a href="https://tools.google.com/dlpage/gaoptout?hl=de" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-hover">
                    https://tools.google.com/dlpage/gaoptout?hl=de
                  </a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-16 py-8 border-t border-border">
        <div className="container mx-auto px-4 text-center">
          <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground mb-4">
            <Link to="/impressum" className="hover:text-primary transition-colors">
              Impressum
            </Link>
            <span>•</span>
            <Link to="/datenschutz" className="hover:text-primary transition-colors">
              Datenschutz
            </Link>
          </div>
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Creativey Media
          </p>
        </div>
      </footer>
    </div>
  );
}
